import _ from 'underscore';

import {SchemaCollaborator} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import notifications, {isResponse} from 'notifications';
import {getStore} from 'redux-stores/Store';
import {RequestSchema} from 'requests/RequestConstants';
import * as RequestSchemaReduxActions from 'requests/schemas/RequestSchemaReduxActions';
import {isRequestSchema} from 'schemas/SchemaPredicates';
import {AssaySchemaSpec, SCHEMA_TYPES, SchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {AssaySchema} from './assay-schemas/AssaySchemaConstants';
import * as AssaySchemaDAO from './assay-schemas/AssaySchemaDAO';

export function dispatchAssaySchemasListed(assaySchemas: Array<AssaySchema | RequestSchema>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ASSAY_SCHEMAS_LISTED,
    assaySchemas,
  });

  const requestSchemasById = _.indexBy(
    assaySchemas.flatMap((schema) => (isRequestSchema(schema) ? [schema] : [])),
    (schema) => schema.id
  );
  getStore().dispatch(RequestSchemaReduxActions.requestSchemasListed(requestSchemasById));
}

export async function fetchAllAssaySchemas(): Promise<void> {
  await AssaySchemaDAO.fetchAll()
    .fail((response) => {
      notifications.notifyError(response);
    })
    .done(({data}: AssaySchemaDAO.FetchAllResponse) => {
      dispatchAssaySchemasListed(data);
    });
}

export function dispatchAssaySchemaFetched(assaySchema: AssaySchema): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ASSAY_SCHEMA_FETCHED,
    assaySchema,
  });
  if (assaySchema.schemaType === SCHEMA_TYPES.ASSAY_REQUEST) {
    getStore().dispatch(RequestSchemaReduxActions.requestSchemaFetched(assaySchema as RequestSchema));
  }
}

export function dispatchAssaySchemasUpdatedFromGQL(assaySchemas: Array<AssaySchema>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.ASSAY_SCHEMAS_UPDATED_FROM_GQL,
    assaySchemas,
  });

  const requestSchemasById = _.indexBy(
    assaySchemas.flatMap((schema) => (isRequestSchema(schema) ? [schema] : [])),
    (schema) => schema.id
  );
  getStore().dispatch(RequestSchemaReduxActions.requestSchemasUpdatedFromGQL(requestSchemasById));
}

function assaySchemaCreated(
  assaySchema: AssaySchema,
  schemaSpec: SchemaSpec,
  schemaType: LegacyAssaySchemaType,
  isSpecJsonFormat?: boolean
): void {
  dispatchAssaySchemaFetched(assaySchema);
  getStore().dispatch(SchemaConfigReduxActions.schemaSpecFetched(assaySchema.id, schemaSpec));
  BenchlingUtil.navigate(null, AssaySchemaDAO.assaySchemaURL(schemaType, assaySchema.id, isSpecJsonFormat));
}

export function createAssaySchema({
  ownerId,
  name,
  spec,
  schemaType,
  sessionId,
  sqlIdentifier = null,
  isSpecJsonFormat = true,
  publishDefaultDataTableConfig = false,
  collaborators = undefined,
}: {
  ownerId: string;
  name: string;
  spec: SchemaSpec;
  schemaType: LegacyAssaySchemaType;
  sessionId: string;
  sqlIdentifier?: string | null;
  isSpecJsonFormat?: boolean;
  publishDefaultDataTableConfig?: boolean;
  collaborators?: Array<SchemaCollaborator>;
}): void {
  LoadingScreen.show();
  AssaySchemaDAO.createAssaySchema({
    ownerId,
    name,
    spec,
    schemaType,
    sessionId,
    sqlIdentifier,
    isSpecJsonFormat,
    publishDefaultDataTableConfig,
    collaborators,
  })
    .fail((response) => {
      getStore().dispatch(SchemaConfigReduxActions.handleSchemaSpecError(response));
    })
    .done(({assaySchema, schemaSpec}: {assaySchema: AssaySchema; schemaSpec: SchemaSpec}) => {
      assaySchemaCreated(assaySchema, schemaSpec, schemaType, isSpecJsonFormat);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function updateAssaySchema({
  schemaId,
  schemaName,
  spec,
  sessionId,
  sqlIdentifier = null,
  isSpecJsonFormat = true,
  publishDefaultDataTableConfig = false,
}: {
  schemaId: string;
  schemaName: string;
  spec: SchemaSpec;
  sessionId: string;
  sqlIdentifier?: string | null;
  isSpecJsonFormat?: boolean;
  publishDefaultDataTableConfig?: boolean;
}): Promise<void> {
  try {
    const {assaySchema, schemaSpec} = await AssaySchemaDAO.updateAssaySchema({
      schemaId,
      schemaName,
      spec,
      sessionId,
      sqlIdentifier,
      isSpecJsonFormat,
      publishDefaultDataTableConfig,
    });
    dispatchAssaySchemaFetched(assaySchema);
    getStore().dispatch(SchemaConfigReduxActions.schemaSpecFetched(assaySchema.id, schemaSpec));
    notifications.success('Updated schema successfully');
  } catch (e) {
    if (isResponse(e)) {
      getStore().dispatch(SchemaConfigReduxActions.handleSchemaSpecError(e));
    }
    throw e;
  }
}

export async function fetchSchemaSpec(schemaId: string, isSpecJsonFormat: boolean = true): Promise<void> {
  await AssaySchemaDAO.fetchSchemaSpec(schemaId, isSpecJsonFormat)
    .fail((response) => {
      notifications.notifyError(response);
    })
    .done(({assaySchema, schemaSpec}: {assaySchema: AssaySchema; schemaSpec: AssaySchemaSpec}) => {
      dispatchAssaySchemaFetched(assaySchema);
      getStore().dispatch(SchemaConfigReduxActions.schemaSpecFetched(assaySchema.id, schemaSpec));
    });
}

export async function updateAssaySchemaCollaborators(
  schemaId: string,
  data: AssaySchemaDAO.UpdateSchemaCollaboratorsRequest
): Promise<void> {
  const response = await AssaySchemaDAO.updateCollaborators(schemaId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.schema]);
}
